import { useState, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { PropertyUnit } from '@/lib/units';
import { formatCurrency, formatFloorDisplay, getStatusBadgeClasses } from '@/lib/utils';
import { usePrefersReducedMotion, useIsMobileWidth } from '@/hooks/useMediaQuery';

const DEFAULT_FALLBACK_IMAGE = '/img/residence-interior.jpg';

interface UnitHeroProps {
  unit: PropertyUnit;
}

export const UnitHero = ({ unit }: UnitHeroProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Accessibility and mobile hooks
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobileWidth(768);

  // Use unit images or fallback to placeholder
  const images = unit.images && unit.images.length > 0 
    ? unit.images 
    : [DEFAULT_FALLBACK_IMAGE];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Handle video error - fall back to image
  const handleVideoError = () => {
    console.warn('Unit hero video failed to load, falling back to image');
    setVideoError(true);
  };

  // Check if unit has video and determine if we should show it
  const hasVideo = !!(unit.hero_video_mp4 || unit.hero_video_webm);
  const shouldShowVideo = hasVideo && !videoError && !prefersReducedMotion && !isMobile;

  // Get the poster/fallback image
  const posterImage = unit.hero_poster_image || images[0];
  const currentImage = images[currentImageIndex];

  const hasVirtualTour = !!(unit.matterport_url || unit.kuula_url);
  const categoryLabel = unit.category === '1bed' ? '1 Bedroom' : 
                       unit.category === '2bed' ? '2 Bedroom' : 
                       unit.category === 'townhouse' ? 'Townhouse' : unit.category;

  return (
    <section className="relative h-[60vh] lg:h-[70vh] overflow-hidden pt-20">
      {/* Background Media (Video or Image) */}
      <div className="absolute inset-0">
        {shouldShowVideo ? (
          // Video Background
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            poster={posterImage}
            className="w-full h-full object-cover"
            onError={handleVideoError}
          >
            {unit.hero_video_webm && (
              <source src={unit.hero_video_webm} type="video/webm" />
            )}
            {unit.hero_video_mp4 && (
              <source src={unit.hero_video_mp4} type="video/mp4" />
            )}
            {/* Fallback for browsers that don't support video */}
            <img
              src={posterImage}
              alt={`Unit ${unit.unit_number} interior view`}
              className="w-full h-full object-cover"
            />
          </video>
        ) : (
          // Static Image Background (with carousel support when no video)
          <img
            src={hasVideo ? posterImage : currentImage}
            alt={`Unit ${unit.unit_number} interior view`}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = DEFAULT_FALLBACK_IMAGE;
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>

      {/* Image Navigation - Only show when not using video */}
      {!shouldShowVideo && images.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-colors"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Image Indicators - Only show when not using video */}
      {!shouldShowVideo && images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentImageIndex ? 'bg-white' : 'bg-white/50'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Content Overlay */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-6 lg:p-8">
        <div className="container mx-auto">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Badge variant="outline" className="text-sm border-white text-white bg-black/30 backdrop-blur-sm">
                {categoryLabel}
              </Badge>
              <Badge 
                className={getStatusBadgeClasses(unit.availability) + ' text-sm'}
              >
                {unit.availability.charAt(0).toUpperCase() + unit.availability.slice(1)}
              </Badge>
              {hasVirtualTour && (
                <Badge variant="outline" className="text-sm border-white text-white bg-black/30 backdrop-blur-sm">
                  ✨ 3D Tour Available
                </Badge>
              )}
              <Badge variant="outline" className="text-sm border-white text-white bg-black/30 backdrop-blur-sm">
                {unit.view_type === 'lagoon' ? 'Lagoon View' : 'Canal View'}
              </Badge>
              <Badge variant="outline" className="text-sm border-white text-white bg-black/30 backdrop-blur-sm">
                {formatFloorDisplay(unit.floor)}
              </Badge>
            </div>
            
            <h1 className="font-display text-4xl lg:text-6xl font-bold text-white mb-4">
              Unit {unit.unit_number}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-white/90 text-lg">
              {unit.area_total && (
                <>
                  <span>{unit.area_total.toLocaleString()} sq ft</span>
                  <span>•</span>
                </>
              )}
              <span>{unit.bedrooms} bed{unit.bedrooms > 1 ? 's' : ''}</span>
              <span>•</span>
              <span>{unit.bathrooms} bath{unit.bathrooms > 1 ? 's' : ''}</span>
              {unit.show_price && unit.price ? (
                <>
                  <span>•</span>
                  <span className="font-semibold text-xl">{formatCurrency(unit.price)}</span>
                </>
              ) : (
                <>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-teal/20 border border-brand-teal/30 text-brand-teal font-semibold text-sm">
                    <span>💎</span>
                    <span>Price on Request</span>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

